import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useSeo from "../seo/useSeo";
import useJsonLd from "../seo/useJsonLd";
import ActionCard from "../components/ActionCard";

export default function HomePage() {
  const navigate = useNavigate();

  /* ================= SEO ================= */
  useSeo({
    title:
      "Pdf-Convert – Convertisseur PDF & Images (JPG/PNG/PDF) | pdf-convert.fr",
    description:
      "Convertissez un PDF en JPG/PNG, transformez des images en PDF A4, compressez vos images et fusionnez vos PDF. Gratuit, rapide et 100% local (sans envoi).",
    canonical: "https://pdf-convert.fr/home",
  });

  /* ================= BREADCRUMB ================= */
  const breadcrumbJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Accueil",
          item: "https://pdf-convert.fr/home",
        },
      ],
    }),
    []
  );
  useJsonLd("breadcrumb-home", breadcrumbJsonLd);

  /* ================= WEB SITE JSON-LD (bonus SEO) ================= */
  const websiteJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Pdf-Convert",
      url: "https://pdf-convert.fr",
      inLanguage: "fr-FR",
    }),
    []
  );
  useJsonLd("website-jsonld", websiteJsonLd);

  return (
    <main style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
      <h1 style={{ textAlign: "center" }}>
        Des outils nécessaires pour travailler sur vos PDF et images
      </h1>

      <p
        style={{
          textAlign: "center",
          color: "#666",
          maxWidth: 760,
          margin: "10px auto 22px",
          lineHeight: 1.5,
        }}
      >
        Convertissez vos fichiers <strong>PDF</strong> et images en quelques
        secondes : Conversion PDF/JPG, Réduction taille d’images, Fusion PDF,
        Signer et annoter des PDF, Supprimer / Réordonner vos PDF. Tout
        fonctionne directement dans votre navigateur : aucun fichier n’est
        envoyé sur un serveur.
      </p>

      {/* ===== 4 CARTES PRINCIPALES ===== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 20,
          marginTop: 18,
        }}
      >
        <ActionCard
          title="PDF → JPG"
          description="Convertir un PDF en images JPG (souvent plus léger)"
          onClick={() => navigate("/pdf-jpg")}
        />

        <ActionCard
          title="PDF → PNG"
          description="Convertir un PDF en images PNG (qualité maximale)"
          onClick={() => navigate("/pdf-png")}
        />

        <ActionCard
          title="JPG / PNG → PDF"
          description="Regrouper une ou plusieurs images en PDF A4"
          onClick={() => navigate("/jpg-pdf")}
        />

        <ActionCard
          title="Réduction d’image"
          description="Compresser une image (JPG/WebP) pour réduire son poids"
          onClick={() => navigate("/reduction")}
        />

        <ActionCard
          title="Fusionner des PDF"
          description="Fusionner et combiner des fichiers PDF"
          onClick={() => navigate("/fusion-pdf")}
        />

        <ActionCard
          title="Fusionner des JPG/PNG en PDF"
          description="Fusionner et combiner des fichiers JPG/PNG en un PDF"
          onClick={() => navigate("/fusion-jpg-pdf")}
        />
        <ActionCard
          title="Supprimer / Réordonner des pages (PDF)"
          description="Supprimer des pages, réorganiser l’ordre, et génèrer un PDF A4 optimisé."
          onClick={() => navigate("/edition-pages-pdf")}
        />
        <ActionCard
          title="Signer / Annoter PDF"
          description="Ajouter des signatures, du texte, des formes, et plus encore à ton PDF."
          onClick={() => navigate("/sign-pdf")}
        />
        <ActionCard
          title="Extraire des pages PDF"
          description="Sélectionner et extraire des pages spécifiques d’un PDF pour créer un nouveau fichier."
          onClick={() => navigate("/split-pdf")}
        />
      </div>

      {/* ===== BLOCS UTILES (SEO + UX) ===== */}
      <section style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 18 }}>Pourquoi Pdf-Convert ?</h2>
        <p style={{ color: "#666", lineHeight: 1.55 }}>
          Pdf-Convert a été conçu pour offrir des outils simples, rapides et
          accessibles, sans inscription. Les conversions sont exécutées
          localement sur votre appareil : vos fichiers restent privés. Selon la
          taille du document et les performances de votre appareil, certaines
          opérations peuvent prendre un peu plus de temps.
        </p>

        <h2 style={{ fontSize: 18, marginTop: 18 }}>Autres outils</h2>
        <p style={{ color: "#666" }}>
          Besoin de regrouper des documents ?{" "}
          <a href="/fusion" style={{ textDecoration: "underline" }}>
            Fusion PDF
          </a>{" "}
          permet de fusionner plusieurs PDF en un seul fichier, ou de regrouper
          des images en PDF.
        </p>

        <p style={{ color: "#666", marginTop: 10 }}>
          Pour comprendre les formats, la qualité ou la confidentialité,
          consultez la{" "}
          <a href="/faq" style={{ textDecoration: "underline" }}>
            FAQ
          </a>{" "}
          et la{" "}
          <a href="/confidentialite" style={{ textDecoration: "underline" }}>
            politique de confidentialité
          </a>
          .
        </p>
      </section>
    </main>
  );
}
